/** @jsx jsx */
import { jsx, ThemeUIStyleObject, Link as ThemeUILink } from "theme-ui";
import React from "react";
import { Link as GatsbyLink, GatsbyLinkProps } from "gatsby";
import { PrimitiveProps } from "@styles/types";

export type LinkProps = PrimitiveProps &
  Omit<GatsbyLinkProps<never>, "ref"> &
  React.ComponentPropsWithoutRef<"a">;

export const linkStyles: ThemeUIStyleObject = {
  textDecoration: "none",
};

const gatsbyLinkStyles: ThemeUIStyleObject = {
  ...linkStyles,
  // <GatsbyLink/> offers an active class indicator - we've hardcoded the class for now
  "&.active": {
    color: "secondary",
  },
};

const ForwardRefGatsbyLink: React.ForwardRefExoticComponent<LinkProps> =
  React.forwardRef(({ sx, to, ...props }, ref) => (
    <GatsbyLink
      to={to}
      activeClassName={"active"}
      sx={{ ...gatsbyLinkStyles, ...sx }}
      {...props}
      ref={ref as never}
    />
  ));

const ForwardRefLink: React.ForwardRefExoticComponent<LinkProps> =
  React.forwardRef(({ sx, to, ...props }, ref) => (
    <ThemeUILink
      href={to}
      sx={{ ...linkStyles, ...sx }}
      {...props}
      ref={ref as never}
    />
  ));

const Link: React.FC<LinkProps> = ({ sx, to, ...props }) => {
  // This assumes that any internal link (intended for Gatsby)
  // will start with exactly one slash, and that anything else is external.
  const internal = /^\/(?!\/)/.test(to);

  if (internal) {
    return <ForwardRefGatsbyLink to={to} {...props} sx={{ ...sx }} />;
  }

  return <ForwardRefLink to={to} {...props} sx={{ ...sx }} />;
};

Link.defaultProps = {};

export { Link, ForwardRefGatsbyLink, ForwardRefLink };
